/**
 * 分享管理器
 * 负责分享到第三方平台的操作
 * @author Joey
 */

import { useTranslation } from "react-i18next";
import { useToast, showToastByCode } from '@/hooks/use-toast';
import { shareDouyinH5, ShareDouyinH5Request } from "@/services/share-douyin-h5";
import { useEffect, useState } from "react";
import { kuaishouWebAuthorizeUrl } from "@/services/kuaishou-web-authorize-url";
import { kuaishouThirdAccount } from "@/services/kuaishou-third-account";
import { kuaishouVideoPublish, KuaishouVideoPublishRequest } from "@/services/kuaishou-video-publish";
import { tiktokThirdAccount } from "@/services/tiktok-third-account";
import { tiktokWebAuthorizeUrl } from "@/services/tiktok-web-authorize-url";
import { TiktokVideoPublishRequest } from "@/services/tiktok-video-upload-file-to-inbox";
import { tiktokVideoPublish } from "@/services/tiktok-video-upload-file-to-inbox";

// 远程接口
class ShareRemote {
  constructor() { }
  // 分享到抖音
  async shareToDouyin({ videoUrls, imageUrls }: ShareDouyinH5Request) {
    return await shareDouyinH5({ videoUrls, imageUrls });
  }
  // 获取快手第三方账号id
  async getKuaishouThirdAccount() {
    return await kuaishouThirdAccount();
  }
  // 快手授权
  async kuaishouWebAuthorizeUrl(redirectUri: string) {
    return await kuaishouWebAuthorizeUrl(redirectUri);
  }
  // 快手发布视频
  async kuaishouVideoPublish({
    brief,
    coverUrl,
    fileUrl,
    thirdAccountIds,
  }: KuaishouVideoPublishRequest) {
    return await kuaishouVideoPublish({
      brief,
      coverUrl,
      fileUrl,
      thirdAccountIds,
    });
  }
  // 获取 TikTok 第三方账号id
  async getTiktokThirdAccount() {
    return await tiktokThirdAccount();
  }
  // 抖音授权
  async tiktokWebAuthorizeUrl(redirectUri: string) {
    return await tiktokWebAuthorizeUrl(redirectUri);
  }
  // TikTok 发布视频
  async tiktokVideoPublish({
    type,
    fileUrl,
    thirdAccountIds,
  }: TiktokVideoPublishRequest) {
    return await tiktokVideoPublish({
      type,
      fileUrl,
      thirdAccountIds,
    });
  }
}

export class ShareManager {
  private static instance: ShareManager;
  private readonly STORAGE_KEY = "MIUTA_SHARE";
  private remote: ShareRemote;

  private constructor() {
    this.remote = new ShareRemote();
  }

  public static getInstance(): ShareManager {
    if (!ShareManager.instance) {
      ShareManager.instance = new ShareManager();
    }
    return ShareManager.instance;
  }

  // 分享到抖音
  async shareToDouyin({ videoUrls, imageUrls }: ShareDouyinH5Request) {
    try {
      return await this.remote.shareToDouyin({
        videoUrls,
        imageUrls,
      });
    } catch (error) {
      console.error("share to douyin failed", error);
      throw error;
    }
  }

  // 获取快手第三方账号id
  async fetchKuaishouThirdAccount() {
    try {
      return await this.remote.getKuaishouThirdAccount();
    } catch (error) {
      console.error("fetch kuaishou third account failed", error);
      throw error;
    }
  }

  // 快手授权
  async kuaishouWebAuthorizeUrl(redirectUri: string) {
    try {
      return await this.remote.kuaishouWebAuthorizeUrl(redirectUri);
    } catch (error) {
      console.error("kuaishou web authorize url failed", error);
      throw error;
    }
  }

  // 快手发布视频
  async kuaishouVideoPublish({
    brief,
    coverUrl,
    fileUrl,
    thirdAccountIds,
  }: KuaishouVideoPublishRequest) {
    try {
      return await this.remote.kuaishouVideoPublish({
        brief,
        coverUrl,
        fileUrl,
        thirdAccountIds,
      });
    } catch (error) {
      console.error("kuaishou video publish failed", error);
      throw error;
    }
  }

  // 获取抖音第三方账号id
  async fetchTiktokThirdAccount() {
    try {
      return await this.remote.getTiktokThirdAccount();
    } catch (error) {
      console.error("fetch tiktok third account failed", error);
      throw error;
    }
  }

  // TikTok 授权
  async tiktokWebAuthorizeUrl(redirectUri: string) {
    try {
      return await this.remote.tiktokWebAuthorizeUrl(redirectUri);
    } catch (error) {
      console.error("tiktok web authorize url failed", error);
      throw error;
    }
  }

  // TikTok 发布视频
  async tiktokVideoPublish({
    type,
    fileUrl,
    thirdAccountIds,
  }: TiktokVideoPublishRequest) {
    try {
      return await this.remote.tiktokVideoPublish({
        type,
        fileUrl,
        thirdAccountIds,
      });
    } catch (error) {
      console.error("tiktok video publish failed", error);
      throw error;
    }
  }

}

// =============================== Hooks ===============================
// 分享到抖音H5
const useShareToDouyin = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [scanUrl, setScanUrl] = useState('');

  const shareToDouyin = async ({ videoUrls, imageUrls }: ShareDouyinH5Request) => {
    if (!videoUrls.length && !imageUrls.length) return;
    if (scanUrl) return scanUrl;
    try {
      setLoading(true);
      const data = await shareManager.shareToDouyin({ videoUrls, imageUrls });
      setScanUrl(data.shareScheme);
      return data.shareScheme;
    } catch (error) {
      console.error("share to douyin failed", error);
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearScanUrl = () => {
    setScanUrl('');
  }

  return {
    shareToDouyin,
    loading,
    scanUrl,
    clearScanUrl,
  }
}

// 分享到快手
export const useShareToKuaishou = () => {
  const { toast, showToastByCode } = useToast();
  const { t } = useTranslation();

  const [shareLoading, setShareLoading] = useState(false);

  const [thirdIdLoading, setThirdIdLoading] = useState(false);
  const [thirdId, setThirdId] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  // 获取快手第三方账号id
  const getKuaishouThirdAccount = async () => {
    try {
      setThirdIdLoading(true);
      const data = await shareManager.fetchKuaishouThirdAccount();
      setThirdId(data.results[0]?.id);
      setIsExpired(data.results[0]?.isExpired);
      return data;
    } catch (error) {
      console.error("get kuaishou third account failed", error);
      showToastByCode(error);
      throw error;
    } finally {
      setThirdIdLoading(false);
    }
  }

  // 分享到快手
  const shareToKuaishou = async ({ brief, coverUrl, fileUrl }) => {
    // 等待获取第三方账号id完成
    if (thirdIdLoading) return;

    if (!brief && !coverUrl && !fileUrl) return;
    try {
      setShareLoading(true);
      // 过期，重新授权
      if (isExpired) {
        const data = await shareManager.kuaishouWebAuthorizeUrl(location.origin + location.pathname);
        window.location.href = data.authorizeUrl;
      } else {
        // 未过期，直接分享
        await shareManager.kuaishouVideoPublish({
          brief,
          coverUrl,
          fileUrl,
          thirdAccountIds: [thirdId]
        });
        toast({
          description: t("agent_share_modal.publish_success"),
        })
      }
    } catch (error) {
      console.error("share to kuaishou failed", error);
      showToastByCode(error);
      throw error;
    } finally {
      setShareLoading(false);
    }
  };

  return {
    shareToKuaishou,
    getKuaishouThirdAccount,
    shareLoading,
    thirdIdLoading,
  }
}

// 分享到 TikTok
export const useShareToTiktok = () => {
  const { toast, showToastByCode } = useToast();
  const { t } = useTranslation();

  const [shareLoading, setShareLoading] = useState(false);

  const [thirdIdLoading, setThirdIdLoading] = useState(false);
  const [thirdId, setThirdId] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  // 获取TikTok第三方账号id
  const getTiktokThirdAccount = async () => {
    try {
      setThirdIdLoading(true);
      const data = await shareManager.fetchTiktokThirdAccount();
      setThirdId(data.results[0]?.id);
      setIsExpired(data.results[0]?.isExpired);
      return data;
    } catch (error) {
      console.error("get tiktok third account failed", error);
      showToastByCode(error);
      throw error;
    } finally {
      setThirdIdLoading(false);
    }
  }

  // 分享到TikTok
  const shareToTiktok = async ({ videoUrls }) => {
    // 等待获取第三方账号id完成
    if (thirdIdLoading) return;

    if (!videoUrls) return;
    try {
      setShareLoading(true);
      // 过期，重新授权
      if (isExpired) {
        const data = await shareManager.tiktokWebAuthorizeUrl(location.origin + location.pathname);
        window.location.href = data.authorizeUrl;
      } else {
        // 未过期，直接分享
        await shareManager.tiktokVideoPublish({
          type: 'video', // 只有分享视频
          fileUrl: videoUrls,
          thirdAccountIds: [thirdId]
        });
        toast({
          description: t("agent_share_modal.tiktok_publish_success"),
        })
      }
    } catch (error) {
      console.error("share to tiktok failed", error);
      showToastByCode(error);
      throw error;
    } finally {
      setShareLoading(false);
    }
  };

  return {
    shareToTiktok,
    getTiktokThirdAccount,
    shareLoading,
    thirdIdLoading,
  }
}

export const shareHook = {
  useShareToDouyin,
  useShareToKuaishou,
  useShareToTiktok,
}
// 导出单例实例
export const shareManager = ShareManager.getInstance();
